import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChartBarIcon,
  UsersIcon,
 
  ClockIcon,
  HeartIcon,

  ShieldCheckIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ArrowPathIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { fetchHospitalProfile, fetchBookingSummary } from '../../../services/MasterAdmin/hospitalService';
import { toggleVendorStatus } from '../../../services/MasterAdmin/vendorService';
import { HospitalProfile } from '../../../models/MasterAdmin/hospital';
import { BookingSummary } from '../../../models/MasterAdmin/bedBooking';
import { format } from 'date-fns';
import FilePreview from '../../../components/MasterAdmin/FilePreview';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: string;
  description?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, trendValue, description }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {trendValue}
          </p>
        )}
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">{icon}</div>
    </div>
  </div>
);

const HospitalVendorDashboard: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [hospitalData, setHospitalData] = useState<HospitalProfile | null>(null);
  const [vendorData, setVendorData] = useState<{ status: string; isActive: boolean } | null>(null);
  const [bookingData, setBookingData] = useState<BookingSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bedTypeFilter, setBedTypeFilter] = useState('all');
  const [showDetails, setShowDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!vendorId) return;
      
      try {
        setLoading(true);
        const [hospitalResponse, bookingResponse] = await Promise.all([
          fetchHospitalProfile(vendorId),
          fetchBookingSummary(vendorId)
        ]);
        setHospitalData(hospitalResponse.data.vendor.Hospital);
        setVendorData({
          status: hospitalResponse.data.vendor.status,
          isActive: hospitalResponse.data.vendor.isActive
        });
        setBookingData(bookingResponse);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vendorId]);

  const handleToggleStatus = async (vendorId: string, currentStatus: string) => {
    try {
      setUpdatingStatus(vendorId);
      const response = await toggleVendorStatus(vendorId);
      if (response.success) {
        setVendorData(prev => {
          if (!prev) return null;
          return {
            ...prev,
            status: response.data.status as 'approved' | 'pending' | 'rejected'
          };
        });
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again later.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !hospitalData || !vendorData) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Hospital data not found'}</p>
      </div>
    );
  }

  const filteredBookings = bookingData?.bookings.filter(booking => {
    const matchesSearch = booking.userDetails.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
    const matchesBedType = bedTypeFilter === 'all' || booking.bedType === bedTypeFilter;
    return matchesSearch && matchesStatus && matchesBedType;
  }) || [];

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setBedTypeFilter('all');
  };

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      {/* Header with Hospital Info */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <BuildingOffice2Icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{hospitalData.name}</h1>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{hospitalData.address}, {hospitalData.city}, {hospitalData.state}</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <PhoneIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{hospitalData.contactNumber}</span>
                  </div>
                  <div className="flex items-center text-gray-600 dark:text-gray-400">
                    <EnvelopeIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{hospitalData.email}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center flex-wrap justify-end gap-2">
              <button
                onClick={() => setShowDetails(true)}
                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                title="View Details"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                vendorData.status === 'approved'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : vendorData.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {vendorData.status.charAt(0).toUpperCase() + vendorData.status.slice(1)}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                vendorData.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {vendorData.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleToggleStatus(hospitalData.vendorId, vendorData.status)}
                disabled={updatingStatus === hospitalData.vendorId}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                  vendorData.status === 'approved'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {updatingStatus === hospitalData.vendorId ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    {vendorData.status === 'approved' ? (
                      <>
                        <ClockIcon className="w-4 h-4" />
                        <span>Mark as Pending</span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Mark as Approve</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <button
              type="button"
              onClick={() => navigate(`/vendors/hospital/${vendorId}/event-timeline`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RectangleStackIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              Event timeline
            </button>
            <button
              type="button"
              onClick={() => navigate(`/vendors/hospital/${vendorId}/login-activity`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ShieldCheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Login activity
            </button>
          </div>
        </div>
      </div>

      {/* Vendor Details Modal */}
      {showDetails && hospitalData && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center border-b dark:border-gray-700 p-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Hospital Details
                </h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  vendorData.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : vendorData.status === 'pending'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}>
                  {vendorData.status.charAt(0).toUpperCase() + vendorData.status.slice(1)}
                </span>
              </div>
              <button 
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
              >
                <XCircleIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-4rem)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Owner Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.ownerName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Phone</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.contactNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Website</label>
                      <p className="mt-1 text-gray-900 dark:text-white">
                        <a href={hospitalData.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                          {hospitalData.website}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Address Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Address</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.address}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Landmark</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.landmark}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">City</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.city}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">State</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.state}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Pincode</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.pincode}</p>
                    </div>
                  </div>
                </div>

                {/* Business Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Business Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">GST Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.gstNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">PAN Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.panNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Working Hours</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.workingTime}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Working Days</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.workingDays}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Fees Range</label>
                      <p className="mt-1 text-gray-900 dark:text-white">₹{hospitalData.feesRange}</p>
                    </div>
                  </div>
                </div>

                {/* Documents and Certifications */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Documents and Certifications</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Certifications</label>
                      <div className="mt-2 space-y-2">
                        {hospitalData.certifications?.map((cert, index) => (
                          <FilePreview 
                            key={index}
                            url={cert.url}
                            name={cert.name}
                            className="w-full"
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Licenses</label>
                      <div className="mt-2 space-y-2">
                        {hospitalData.licenses?.map((license, index) => (
                          <FilePreview 
                            key={index}
                            url={license.url}
                            name={license.name}
                            className="w-full"
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Photos</label>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {hospitalData.photos?.map((photo, index) => (
                          <FilePreview 
                            key={index}
                            url={photo.url}
                            name={photo.name}
                            className="w-full"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hospital Specific Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Hospital Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Beds Available</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.bedsAvailable}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Specialties</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {hospitalData.specialityTypes.map((specialty, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Services Offered</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {hospitalData.servicesOffered.map((service, index) => (
                          <span key={index} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Insurance Companies</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {hospitalData.insuranceCompanies.map((company, index) => (
                          <span key={index} className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">
                            {company}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Facilities */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Facilities</h4>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-500 dark:text-gray-400">Lift Access</label>
                        <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.hasLiftAccess ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500 dark:text-gray-400">Parking</label>
                        <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.hasParking ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500 dark:text-gray-400">Wheelchair Access</label>
                        <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.hasWheelchairAccess ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500 dark:text-gray-400">Ambulance Service</label>
                        <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.providesAmbulanceService ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-gray-500 dark:text-gray-400">Online Consultancy</label>
                        <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.providesOnlineConsultancy ? 'Yes' : 'No'}</p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Other Facilities</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {hospitalData.otherFacilities.map((facility, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 rounded-full text-sm">
                            {facility}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* About */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">About</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Description</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{hospitalData.about}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Core Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Beds"
          value={hospitalData.bedsAvailable}
          icon={<UsersIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
          description="Available beds"
        />
        <MetricCard
          title="Specialties"
          value={hospitalData.specialityTypes.length}
          icon={<HeartIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
          description="Medical specialties"
        />
        <MetricCard
          title="Services"
          value={hospitalData.servicesOffered.length}
          icon={<ChartBarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />}
          description="Services offered"
        />
        <MetricCard
          title="Insurance Partners"
          value={hospitalData.insuranceCompanies.length}
          icon={<ShieldCheckIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
          description="Insurance companies"
        />
      </div>

      {/* Booking Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <MetricCard
          title="Pending Bookings"
          value={bookingData?.counts.pending || 0}
          icon={<ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
          description="Awaiting confirmation"
        />
        <MetricCard
          title="Completed Bookings"
          value={bookingData?.counts.completed || 0}
          icon={<CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
          description="Successful bookings"
        />
        <MetricCard
          title="Cancelled Bookings"
          value={bookingData?.counts.cancelled || 0}
          icon={<XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />}
          description="Cancelled bookings"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Recent Bookings */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <div className="flex flex-col space-y-4 mb-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Bookings</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {Math.ceil((filteredBookings.length || 0) / 3)}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil((filteredBookings.length || 0) / 3)))}
                    disabled={currentPage >= Math.ceil((filteredBookings.length || 0) / 3)}
                    className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Search and Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                {/* Search Bar */}
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search by patient name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full md:w-40 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                {/* Bed Type Filter */}
                <select
                  value={bedTypeFilter}
                  onChange={(e) => setBedTypeFilter(e.target.value)}
                  className="block w-full md:w-40 pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="all">All Bed Types</option>
                  {Array.from(new Set(bookingData?.bookings.map(b => b.bedType) || [])).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                {/* Clear Filters Button */}
                {(searchQuery || statusFilter !== 'all' || bedTypeFilter !== 'all') && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {filteredBookings
                .slice((currentPage - 1) * 3, currentPage * 3)
                .map((booking) => (
                  <div key={booking.bedBookingId} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white">{booking.userDetails.name}</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{booking.userDetails.phone_number}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        booking.status === 'completed' 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : booking.status === 'pending'
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      }`}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Bed Type</p>
                        <p className="text-sm text-gray-900 dark:text-white">{booking.bedType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Price</p>
                        <p className="text-sm text-gray-900 dark:text-white">₹{booking.price}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Booking Date</p>
                        <p className="text-sm text-gray-900 dark:text-white">{format(new Date(booking.bookingDate), 'dd MMM yyyy')}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Time Slot</p>
                        <p className="text-sm text-gray-900 dark:text-white">{booking.timeSlot}</p>
                      </div>
                    </div>
                  </div>
                ))}

              {filteredBookings.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">No bookings found matching your filters.</p>
                </div>
              )}
            </div>
          </div>

          {/* Specialties & Services */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Specialties & Services</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Specialties</h3>
                <div className="space-y-2">
                  {hospitalData.specialityTypes.map((specialty) => (
                    <div key={specialty} className="flex items-center">
                      <HeartIcon className="h-4 w-4 text-red-500 dark:text-red-400 mr-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{specialty}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">Services</h3>
                <div className="space-y-2">
                  {hospitalData.servicesOffered.map((service) => (
                    <div key={service} className="flex items-center">
                      <ChartBarIcon className="h-4 w-4 text-blue-500 dark:text-blue-400 mr-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Insurance Partners */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Insurance Partners</h2>
            <div className="space-y-3">
              {hospitalData.insuranceCompanies.map((company) => (
                <div key={company} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">{company}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* About */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">About</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">{hospitalData.about}</p>
          </div>

          {/* Contact Information */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Contact Information</h2>
            <div className="space-y-3">
              <div className="flex items-center">
                <PhoneIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{hospitalData.contactNumber}</span>
              </div>
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{hospitalData.email}</span>
              </div>
              <div className="flex items-center">
                <MapPinIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {hospitalData.address}, {hospitalData.landmark}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalVendorDashboard; 